import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {IGlobalStorefrontSettings, IStorefrontQuestion, IStorefrontSettings} from '../../models/Storefront';
import ToggleField from "../Common/ToggleField";
import {
    useCreateStorefrontQuestionMutation,
    useDeleteStorefrontQuestionMutation,
    useGetStorefrontQuestionsQuery,
    useSaveStorefrontQuestionMutation
} from "../../store/endpoints/StorefrontQuestionEndpoints";
import StorefrontQuestionEditor from "./StorefrontQuestionEditor";
import {
    CommandBar,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    ICommandBarItemProps,
    IconButton,
    IContextualMenuProps,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack
} from "@fluentui/react";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import Accordion from "../Common/Accordion";

interface IStorefrontQuestionsFormProps {
    globalSettings?: IGlobalStorefrontSettings;
    settings: IStorefrontSettings;
    onChange: (settings: IStorefrontSettings) => void;
    onLoading?: (isLoading: boolean) => void;
}

const StorefrontQuestionsForm: React.FC<IStorefrontQuestionsFormProps> = (props) => {
    
    const [globalQuestionDetails, setGlobalQuestionDetails] = useState<IStorefrontQuestion[]>([]);
    const [currentSelectedGlobalQuestionIds, setCurrentSelectedGlobalQuestionIds] = useState<string[]>([]);
    
    const storefrontQuestions = useGetStorefrontQuestionsQuery(props.settings.id ?? '', {skip: !props.settings.id || props.settings.id === ''})
    
    const globalQuestions =  useGetStorefrontQuestionsQuery('global')
    const [saver] = useSaveStorefrontQuestionMutation();
    const [createNewQuestion] = useCreateStorefrontQuestionMutation();
    const [saving, setSaving] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [questionEditorError, setQuestionEditorError] = useState<string>();
    const [error, setError] = useState<string>();
    const [showEditor, setShowEditor] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [deleter] = useDeleteStorefrontQuestionMutation()
    const [selectedQuestion, setSelectedQuestion] = useState<IStorefrontQuestion>();
    const [showGlobalPicker, setShowGlobalPicker] = useState(false);
    const [idToDelete, setIdToDelete] = useState<string>();
    const [idToRemove, setIdToRemove] = useState<string>();

    useEffect(() => {
        return () => {
            setShowGlobalPicker(false);
            setSaving(false);
            setIdToDelete(undefined);
            setIdToRemove(undefined);
            setSelectedQuestion(undefined);
            setError(undefined);
            setShowEditor(false);
            setQuestionEditorError(undefined);
        }
    }, []);

    useEffect(() => {
        setCurrentSelectedGlobalQuestionIds(props.settings.checkoutQuestionIds ?? []);
    }, [props.settings.checkoutQuestionIds]);

    /*
     * A per-question deep link names the question inside the setting id
     * (`storefront.orderQuestion.<questionId>.<field>`), so open that question's editor rather than
     * leaving the panel closed. Without this the link lands on the Order Questions tab with no
     * question selected and the field it points at is never rendered.
     */
    const location = useLocation();
    const focusedQuestionId = React.useMemo(() => {
        const focus = new URLSearchParams(location.search).get('focus');
        return /^storefront\.orderQuestion\.([^.]+)\./.exec(focus ?? '')?.[1];
    }, [location.search]);
    useEffect(() => {
        if (!focusedQuestionId) return;
        const match = storefrontQuestions.data?.items.find((q) => q.id === focusedQuestionId);
        if (match) {
            setSelectedQuestion(match);
            setShowEditor(true);
        }
    }, [focusedQuestionId, storefrontQuestions.data]);

    useEffect(() => {
        if(currentSelectedGlobalQuestionIds && currentSelectedGlobalQuestionIds.length > 0 && globalQuestions.data) {
            const newDetails: IStorefrontQuestion[] = []
            currentSelectedGlobalQuestionIds.map(a => {
                const match = globalQuestions.data?.items?.find(q => q.id === a);
                if (match) {
                    newDetails.push(match);
                }
            });
            setGlobalQuestionDetails(() => [...newDetails]);
        }
        else{
            setGlobalQuestionDetails([])
        }
        
    }, [globalQuestions.data, currentSelectedGlobalQuestionIds]);

    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    const getNewQuestionId = () => {
        const sequence = (storefrontQuestions.data?.items.length ?? 0) + 1;
        
        const id = `Question-${sequence}`;
        
        if(storefrontQuestions.data?.items.findIndex(a => a.identifier == id)){
            return crypto.randomUUID()
        }
        return id;
    }
    
    const headerCommands: ICommandBarItemProps[] = [
        {
            key: 'addQuestion',
            text: 'Add Ad-Hoc Question',
            title: 'Add Ad-Hoc Question',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {
                if(props.settings.id) {
                    const newQuestion = {
                        identifier: getNewQuestionId(),
                        questionText: '',
                        answers: [],
                        entityType: 'storefront',
                        entityId: props.settings.id
                    }
                    setSaving(true);
                    createNewQuestion({question: newQuestion, storefront: props.settings.id})
                        .unwrap()
                        .then((r) => {
                            setError(undefined);
                            setSaving(false);
                            scrollToBottom()
                            setSelectedQuestion({...newQuestion, id: r.id })
                            setShowEditor(true)
                        })
                        .catch((error) => {
                            // Error: error contains the error object
                            setError(error?.data?.message ?? 'Unknown error creating question');
                            setSaving(false);
                        });
                }
            }
        },
        {
            key: 'addGlobalQuestion',
            text: 'Add Global Question',
            title: 'Add Question',
            disabled: saving || !globalQuestions?.data?.items || globalQuestions?.data?.items.length === 0,
            iconProps: {iconName: 'DependencyAdd'},
            onClick: () => {
                setShowGlobalPicker(true);
            }
        }
    ]

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save();
                    setSelectedQuestion(undefined);
                    setShowEditor(false);
                }
            }
        ],
    };



    const footer = () => (
        <div style={{background: "white"}}>
            <Stack horizontal tokens={{childrenGap: '1em'}}>
                <PrimaryButton split menuProps={menuProps} text='Save' onClick={() => save()} disabled={saving || selectedQuestion?.entityId == 'global'} />
                <DefaultButton 
                    text='Cancel' 
                    onClick={() => {
                        setSelectedQuestion(undefined)
                        setShowEditor(false)
                        setSaving(false)
                    }}
                    disabled={saving} />
            </Stack>
        </div>
    )

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
            <h1>Edit Storefront Question</h1>
            {questionEditorError &&
                <MessageBar messageBarType={MessageBarType.error}>{questionEditorError}</MessageBar>
            }
        </div>
    )

    const pickerHeader = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
            <h1>Pick Global Storefront Question</h1>
        </div>
    )
    
    const save = () => {
        setSaving(true)
        if(props.settings.id && selectedQuestion?.id) {
            saver({question: selectedQuestion, storefront: props.settings.id, questionId: selectedQuestion.id}).unwrap()
                .then((e) => {
                    setQuestionEditorError(undefined);

                    if(e.success == false){
                        setQuestionEditorError(e.message ?? 'Unknown error saving question')
                    }
                    setSaving(false)
                })
                .catch((error) => {
                    // Error: error contains the error object
                    setQuestionEditorError(error?.data?.message ?? 'Unknown error creating question');
                    setSaving(false);
                });
        }
    }
    
    const removeSelectedGlobalId = (id: string) => {
        const currentQuestionIndex = currentSelectedGlobalQuestionIds?.findIndex(a => a === id)
        if(currentQuestionIndex > -1) {
            const newQuestionIds = [...currentSelectedGlobalQuestionIds]
            newQuestionIds.splice(currentQuestionIndex, 1)
            setCurrentSelectedGlobalQuestionIds([...newQuestionIds])
            props.onChange({
                ...props.settings,
                checkoutQuestionIds: newQuestionIds
            })
        }
    }
    
    const deleteQuestion = (id: string) => {
        if(selectedQuestion?.entityId === 'global'){
            return;
        }
        if(props.settings.id) {
            deleter({storefront: props.settings.id, questionId: id})
        }
        
        setIdToDelete(undefined)
    }

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='edit-storefront-question' iconProps={{iconName: 'edit'}} onClick={() => {
                        setSelectedQuestion(q)
                        setShowEditor(true)
                    }}/>,
                required: true,
            },
            {
                identifier: 'identifier',
                label: 'Identifier',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.identifier}</div>,
            },
            {
                identifier: 'questionText',
                label: 'Question Text',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.questionText}</div>,

            },
            {
                identifier: 'required',
                label: 'Required',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.isRequired ? ('True') : ('False')}</div>,
            },
            {
                identifier: 'Delete',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='delete-storefront-question' iconProps={{iconName: 'Delete'}} onClick={() => {
                        if(q.id) {
                            setIdToDelete(q.id)
                            setShowConfirmDelete(true)
                        }
                    }}/>,
                required: true,
            },
        ];
        return columnDetails
    }

    const getGlobalPickerColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'select',
                label: '',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <IconButton
                    data-testid='attach-global-question'
                    iconProps={{iconName: 'add'}}
                    onClick={() => {
                        if(!q.id || currentSelectedGlobalQuestionIds.includes(q.id) ) {
                            setShowGlobalPicker(false)
                            return
                        }
                        props.onChange({...props.settings, checkoutQuestionIds: [...currentSelectedGlobalQuestionIds , q.id]})
                        setShowGlobalPicker(false)
                    }
                }/>,
            },
            {
                index: 1,
                identifier: 'identifier',
                label: 'Identifier',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.identifier}</div>,
            },
            {
                index: 2,
                identifier: 'questionText',
                label: 'Question Text',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.questionText}</div>,

            },
            {
                index: 3,
                identifier: 'required',
                label: 'Required',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.isRequired ? ('True') : ('False')}</div>,
            }
        ];
        return columnDetails
    }

    const getGlobalColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='view-global-question' iconProps={{iconName: 'EntryView'}} onClick={() => {
                        setSelectedQuestion(q)
                        setShowEditor(true)
                    }}/>,
                required: true,
            },
            {
                identifier: 'identifier',
                label: 'Identifier',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.identifier}</div>,
            },
            {
                identifier: 'questionText',
                label: 'Question Text',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.questionText}</div>,

            },
            {
                identifier: 'required',
                label: 'Required',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.isRequired ? ('True') : ('False')}</div>,
            },
            {
                identifier: 'Copy',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='copy-global-question' iconProps={{iconName: 'Copy'}} onClick={() => {
                        if(props.settings.id) {
                            const newQuestion = {
                                identifier: getNewQuestionId(),
                                questionText: q.questionText,
                                answers: q.answers,
                                entityType: 'storefront',
                                entityId: props.settings.id
                            }
                            createNewQuestion({question: newQuestion, storefront: props.settings.id})
                                .unwrap()
                                .then((r) => {
                                    setError(undefined);

                                    if(q.id) {
                                        removeSelectedGlobalId(q.id)
                                    }
                                    setSaving(false);
                                    scrollToBottom()
                                    setSelectedQuestion({...newQuestion, id: r.id})
                                })
                                .catch((error) => {
                                    setError(error?.data?.message ?? 'Unknown error creating question');
                                    setSaving(false);
                                });
                        }
                    }}/>,
                        
                required: true,
            },
            {
                identifier: 'Delete',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='detach-global-question' iconProps={{iconName: 'Delete'}} onClick={() => {
                        setIdToRemove(q.id)
                        setShowConfirmRemove(true)
                    }}/>,
                required: true,
            },
        ];
        return columnDetails
    }

    return (
        <div>
            <Panel
                onOuterClick={() => {console.log("swallowing modal close event")}}
                type={PanelType.large}
                headerText={'Pick Global Question'}
                isOpen={showGlobalPicker}
                onDismiss={() => setShowGlobalPicker(false)}
                closeButtonAriaLabel='Close'
                onRenderHeader={pickerHeader}
                styles={{
                    commands: {
                        background: "white"
                    },
                    footer: {
                        background: "white"
                    },
                    navigation: {
                        flex: '1 1 auto',
                    },
                }}
            >
                <DataGrid 
                    getItemId={(item: IStorefrontQuestion) => item.id ?? ''}
                    items={globalQuestions?.data?.items ?? []} 
                    loading={globalQuestions.isLoading || globalQuestions.isFetching} 
                    selectionMode={GridSelectionMode.none}
                    overrideColumns={getGlobalPickerColumns()} 
                    useContentHeight={true}
                />

            </Panel>
            <Dialog
                hidden={!showConfirmDelete}
                onDismiss={() => setShowConfirmDelete(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Question',
                    subText: 'Are you sure you want to delete this question?',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            if(idToDelete) {
                                deleteQuestion(idToDelete)
                                setShowConfirmDelete(false)
                            }
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmDelete(false)}
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!showConfirmRemove}
                onDismiss={() => setShowConfirmRemove(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Question',
                    subText: 'Are you sure you want to remove this question?',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            if(idToRemove) {
                                removeSelectedGlobalId(idToRemove)
                                setShowConfirmRemove(false)
                            }
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmRemove(false)}
                    />
                </DialogFooter>
            </Dialog>
            <Panel
                onOuterClick={() => {console.log("swallowing modal close event")}}
                type={PanelType.large}
                headerText={selectedQuestion?.entityId === 'global' ? 'View Storefront Question' : 'Edit Storefront Question'}
                isOpen={showEditor}
                onDismiss={() => setShowEditor(false)}
                closeButtonAriaLabel='Close'
                isFooterAtBottom={true}
                onRenderFooterContent={footer}
                onRenderHeader={header}
                styles={{
                    commands: {
                        background: "white"
                    },
                    footer: {
                        background: "white"
                    },
                    navigation: {
                        flex: '1 1 auto',
                    },
                }}
            >
                <StorefrontQuestionEditor
                    question={selectedQuestion}
                    canEdit={selectedQuestion?.entityId != 'global'}
                    // A global question opened for view here is the same setting it is on the Global
                    // Questions screen, so it reuses that screen's id rather than minting a second one.
                    settingKeyPrefix={selectedQuestion?.entityId === 'global' ? 'storefront.global.orderQuestion' : 'storefront.orderQuestion'}
                    onChange={(q) => {
                        setSelectedQuestion({...q})
                    }}
                />
                
            </Panel>
            {(storefrontQuestions.isFetching || storefrontQuestions.isLoading) ? <Spinner label={'Loading Questions...'} size={SpinnerSize.large}/> :
            <React.Fragment>
                {error &&
                    <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                }
                <CommandBar items={headerCommands} farItems={[]}/>
                <ToggleField
                    inline={true}
                    label={'Ignore Product Questions'}
                    checked={props.settings.ignoreProductQuestions}
                    onChange={(_,v) => {
                        props.onChange({...props.settings, ignoreProductQuestions: v});
                    }}
                />
                <ToggleField
                    inline={true}
                    label={'Ignore Bundle Questions'}
                    checked={props.settings.ignoreBundleQuestions}
                    onChange={(_,v) => {
                        props.onChange({...props.settings, ignoreBundleQuestions: v});
                    }}
                />
                <Accordion useChevron={true} titleComponent={<h3>Ad-Hoc Questions</h3>} beginExpanded={true}>
                    <DataGrid
                        getItemId={(item: IStorefrontQuestion) => item.id ?? ''} 
                        items={storefrontQuestions.data?.items ?? []}
                        selectionMode={GridSelectionMode.none}
                        loading={storefrontQuestions.isFetching || storefrontQuestions.isLoading}
                        overrideColumns={getCustomColumns()}
                        useContentHeight={true}
                        />
                </Accordion>
                <Accordion useChevron={true} titleComponent={<h3>Global Questions</h3>} beginExpanded={true}>
                    <DataGrid
                        getItemId={(item: IStorefrontQuestion) => item.id ?? ''}
                        items={globalQuestionDetails ?? []}
                        selectionMode={GridSelectionMode.none}
                        loading={globalQuestions.isFetching || globalQuestions.isLoading}
                        overrideColumns={getGlobalColumns()}
                        useContentHeight={true}
                        />
                </Accordion>
            </React.Fragment>
            }
            <div ref={bottomRef} />
        </div>
    );
}

export default StorefrontQuestionsForm;